import {Component, Inject, Input, OnChanges} from "@angular/core";
import * as moment from "moment";
import {Aircraft} from "../aircraft";
import {Device} from "../device";
import {DeviceAvdbType, DeviceAvdbTypeStatus} from "../device-avdb-type";
import {ActivatedRoute, ParamMap} from "@angular/router";
import { PermissionUtil } from "../shared/permission-util.service";

@Component({
	selector: "device-row",
	template: `
		<div [ngClass]="{
				'devicerow': true,
				'devicerow-portable': device.model.portable,
				'devicerow-panelmount': !device.model.portable,
				'devicerow-compact': compactView
			}" [routerLink]="deviceLink">

			<div class="devicerow_content">
				<div class="devicerow_details">
					<image-container class="devicerow_image" *ngIf="device.model && !compactView"
									 [width]="100"
									 [height]="80"
									 [src]="device.model.imageURL"
									 [alt]="device.model.displayName"></image-container>

					<div [class]="(compactView) ? 'devicerow_text devicerow_text-compact' : 'devicerow_text'">
						<h3><a [routerLink]="deviceLink"
							   (click)="$event.stopPropagation()">{{device.name}}
						</a></h3>
						<p *ngIf="device.model">{{device.model.make}} {{device.model.name}}</p>
					</div>
				</div>

				<div *ngIf="device.firstUnredeemedBundle && (aircraft == null || hasAvdbPurchasePermission) && !this.isDealerCustomerManagementFlow; else noBundle">
					<a *ngIf="!compactView"
					   [routerLink]="device.redeemBundleLink(device.firstUnredeemedBundle.bundle)"
					   (click)="$event.stopPropagation()"
					   class="button button-primary">
						Required Action: Redeem {{device.firstUnredeemedBundle.kind}}
					</a>
				</div>

				<ng-template #noBundle>
					<div *ngIf="shouldShowStatus" [ngClass]="['devicerow_status', statusClass]">
						<i *ngIf="hasCheckIcon" class="fa fa-check-circle"></i>
						<i *ngIf="hasExclamationIcon" class="fa fa-exclamation-circle"></i>
						<span *ngIf="numUpdates">{{numUpdates}}</span>
						{{statusText}}
						<p *ngIf="shouldShowLatestUpdate" class="center_text">{{lastUpdatedDate}}</p>
						<p *ngIf="nextUpdateText && !compactView">{{nextUpdateText}}</p>
						<a *ngIf="device.purchasedButNotInstalledAvdbs.length > 0 && !compactView && !isDealerCustomerManagementFlow"
						   [routerLink]="['/update-databases', {deviceID: device.id}]"
						   (click)="$event.stopPropagation()"
						   class="devicerow_updatebutton button button-primary">
							Install Updates
						</a>
					</div>
				</ng-template>
			</div>

			<div class="devicerow_rightarrow">
				<i class="fa fa-angle-right fa-2x"></i>
			</div>

		</div>
	`,
})
export class DeviceRowComponent implements OnChanges {
	@Input() device: Device;
	@Input() aircraft: Aircraft;
	@Input() compactView: boolean;

	customerGuid: string = null;
	shouldShowStatus: boolean;
	statusClass: string;
	hasCheckIcon: boolean;
	hasExclamationIcon: boolean;
	isDealerCustomerManagementFlow: boolean = false;
	isDealerUser: boolean;
	numUpdates: number;
	statusText: string;
	nextUpdateText: string;
	shouldShowLatestUpdate: boolean;
	hasAvdbPurchasePermission = false;

	constructor(
		private route: ActivatedRoute,
		public permissionUtil: PermissionUtil,
		@Inject("isDealerUser") isDealerUser: boolean
	) {
		this.isDealerUser = isDealerUser;
	}

	ngOnInit() {
		this.route.paramMap.subscribe((routeParams: ParamMap) => {
			this.customerGuid = routeParams.get("customer-uuid");
		});
		this.isDealerCustomerManagementFlow = this.isDealerUser && this.customerGuid != null;
		this.permissionUtil.hasAvdbPurchasePermission(this.aircraft)
			.subscribe((hasPermission) => this.hasAvdbPurchasePermission = hasPermission);
	}

	ngOnChanges() {
		this.getAvdbStatus(this.device.avdbStatus);
	}

	private getAvdbStatus(avdbStatus: string) {
		let nextUpdate;
		if (this.device.nextExpectedAvdbAvailability) {
			nextUpdate = moment(this.device.nextExpectedAvdbAvailability).fromNow();
		}
		switch (avdbStatus) {
			case DeviceAvdbTypeStatus.NOT_INSTALLED:
				if (this.hasPurchasedButNotInstalledAvdbs()) {
					this.shouldShowStatus = true;
					this.statusClass = "devicerow_status-notinstalled";
					this.numUpdates = this.device.purchasedButNotInstalledAvdbs.length;
					this.statusText = "Ready to Install";
					if (nextUpdate)
						this.nextUpdateText = `Next update available ${nextUpdate}.`;
				}
				break;
			case DeviceAvdbTypeStatus.LATEST:
				this.shouldShowStatus = true;
				this.statusClass = "devicerow_status-latest";
				this.hasCheckIcon = true;
				this.statusText = "Database(s) Updated On";
				if (nextUpdate)
					this.nextUpdateText = `Next update available ${nextUpdate}.`;
				this.shouldShowLatestUpdate = true;
				break;
			case DeviceAvdbTypeStatus.OUTDATED:
				this.shouldShowStatus = true;
				this.hasCheckIcon = false;
				this.shouldShowLatestUpdate = false;
				this.statusClass = "devicerow_status-outdated";
				const outdatedAvdbs = this.getDatabasesByStatus(DeviceAvdbTypeStatus.OUTDATED);
				this.numUpdates = outdatedAvdbs.length;
				this.statusText = (this.numUpdates === 1) ? "Update Available" : "Updates Available";
				this.hasExclamationIcon = false;
				const duration = this.getDurationFromInvalidAt(outdatedAvdbs);
				if (duration != null) {
					this.nextUpdateText = `${outdatedAvdbs.length} will expire ${duration}.`;
				} else {
					this.nextUpdateText = "";
				}
				break;
			case DeviceAvdbTypeStatus.EXPIRED:
				this.shouldShowStatus = true;
				this.hasCheckIcon = false;
				this.shouldShowLatestUpdate = false;
				this.statusClass = "devicerow_status-expired";
				const expiredAvdbs = this.getDatabasesByStatus(DeviceAvdbTypeStatus.EXPIRED);
				this.numUpdates = expiredAvdbs.length;
				this.statusText = (this.numUpdates === 1) ? "Update Required" : "Updates Required";
				this.nextUpdateText = `${expiredAvdbs.length} expired ${this.getDurationFromInvalidAt(expiredAvdbs)}.`;
				this.hasExclamationIcon = false;
				break;
			default:
				this.shouldShowLatestUpdate = false;
				this.shouldShowStatus = false;
		}
	}

	private hasPurchasedButNotInstalledAvdbs(): boolean {
		return this.device.purchasedButNotInstalledAvdbs && this.device.purchasedButNotInstalledAvdbs.length > 0;
	}

	get lastUpdatedDate(): string {
		const installedDates = this.device.avdbTypes.filter(avdb => avdb.installedIssue != null && avdb.installedIssue.installedAt != null)
			.map(function(avdb) {
				return moment(avdb.installedIssue.installedAt);
			});
		return moment.max(installedDates).format('DD-MMM-YYYY h:mm:ss a');
	}

	get deviceLink() {
		if (this.isDealerCustomerManagementFlow) {
			return ["/customer/", this.customerGuid, "device", this.device.id];
		} else {
			return ["/details/", this.device.id];
		}
	}

	/**
	 * Filter avdbs by status
	 * @param avdbStatus
	 */
	private getDatabasesByStatus(avdbStatus: DeviceAvdbTypeStatus) {
			return this.device.avdbTypes.filter(avdbType => avdbType.status === avdbStatus);
	}

	private getDurationFromInvalidAt(avdbs: Array<DeviceAvdbType>): string {
		if(!avdbs || avdbs.length == 0){
			return null;
		}
		const allInvalidAts = avdbs.filter(avdb => avdb.installedIssue.invalidAt)
			.map(function(avdb) {
				return moment(avdb.installedIssue.invalidAt);
			});
		const earliestInvalidAt = moment.min(allInvalidAts);

		if (earliestInvalidAt) {
			return earliestInvalidAt.fromNow();
		}
		else {
			return null;
		}
	}
}
